import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  FaUndoAlt,
  FaFileInvoiceDollar,
  FaSearch,
  FaArrowRight,
  FaBoxOpen,
  FaMoneyBillWave,
  FaShieldAlt,
} from "react-icons/fa";

export default function RefundViewMain() {
  const [loading, setLoading] = useState(true);
  const [refundOrders, setRefundOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, full, item

  useEffect(() => {
    document.title = "Refunds & Returns — Admin";
    fetchRefunds();
  }, []);

  const fetchRefunds = () => {
    setLoading(true);
    axios.get(`/api/fetch-refund-order`).then((res) => {
      if (res.data.status === 200) {
        setRefundOrders(res.data.data || []);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const isItemFullRefund = (item) => {
    return (
      item.order_status === "Refunded" ||
      item.status == 5 ||
      (parseFloat(item.total_refunded) >= (parseFloat(item.total_amount) - 0.01) && parseFloat(item.total_amount) > 0)
    );
  };

  // Filtered dataset
  const filteredList = refundOrders.filter((item) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      (item.tracking_no && item.tracking_no.toLowerCase().includes(term)) ||
      (item.firstname && item.firstname.toLowerCase().includes(term)) ||
      (item.lastname && item.lastname.toLowerCase().includes(term)) ||
      (item.email && item.email.toLowerCase().includes(term)) ||
      (item.phone && item.phone.toLowerCase().includes(term));

    const isFull = isItemFullRefund(item);
    const matchesType =
      filterType === "all" ||
      (filterType === "full" && isFull) ||
      (filterType === "item" && !isFull);

    return matchesSearch && matchesType;
  });

  const fullRefundCount = refundOrders.filter(isItemFullRefund).length;
  const itemRefundCount = refundOrders.length - fullRefundCount;
  const totalDisbursedAll = refundOrders.reduce((sum, item) => sum + parseFloat(item.total_refunded || 0), 0);

  return (
    <div className="container-fluid px-0">
      {/* Page Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Refunds & Customer Returns
            </h3>
          </div>
          <p className="text-muted small mb-0 mt-1">
            Authoritative tracking of customer returns, warehouse inspections, and refund disbursements.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/refund/sale-orders"
            className="btn btn-primary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 shadow-sm rounded-3"
          >
            <FaFileInvoiceDollar /> View Sales Orders
          </Link>
          <Link
            to="/admin/orders"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaBoxOpen /> Order Manager
          </Link>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Total Processed Refunds</span>
              <div className="admin-stat-icon" style={{ background: "#fee2e2", color: "#dc2626" }}>
                <FaUndoAlt />
              </div>
            </div>
            <div className="admin-stat-value text-danger">{refundOrders.length}</div>
            <div className="admin-stat-footer text-muted">
              <FaShieldAlt className="text-success me-1" /> Rs. {totalDisbursedAll.toLocaleString()} Reconciled in GL
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Full Order Refunds</span>
              <div className="admin-stat-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
                <FaMoneyBillWave />
              </div>
            </div>
            <div className="admin-stat-value text-dark">{fullRefundCount}</div>
            <div className="admin-stat-footer text-muted">
              Complete order total reversals
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Item-Specific Returns</span>
              <div className="admin-stat-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}>
                <FaBoxOpen />
              </div>
            </div>
            <div className="admin-stat-value text-primary">{itemRefundCount}</div>
            <div className="admin-stat-footer text-muted">
              Partial line-item inspection & refunds
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="admin-card">
        {/* Filter and Search Bar */}
        <div className="admin-card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
          {/* Tabs */}
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => setFilterType("all")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                filterType === "all" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              All Refunds ({refundOrders.length})
            </button>
            <button
              onClick={() => setFilterType("full")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                filterType === "full" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              Full Order ({fullRefundCount})
            </button>
            <button
              onClick={() => setFilterType("item")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                filterType === "item" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              Partial Items ({itemRefundCount})
            </button>
          </div>

          {/* Search box */}
          <div className="position-relative" style={{ minWidth: "280px" }}>
            <FaSearch
              className="position-absolute text-muted"
              style={{ top: "11px", left: "12px", fontSize: "14px" }}
            />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control form-control-sm ps-5 rounded-pill border-light-subtle shadow-none"
              placeholder="Search by Tracking #, Name, Email..."
            />
          </div>
        </div>

        {/* Table / Content */}
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="text-muted mt-2 small">Loading refund transactions...</p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-5">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light mb-3"
                style={{ width: "64px", height: "64px", color: "#94a3b8" }}
              >
                <FaUndoAlt size={24} />
              </div>
              <h6 className="fw-bold text-dark">No refund records found</h6>
              <p className="text-muted small mb-0">
                {searchTerm
                  ? "No results matching your search criteria."
                  : "All customer purchases are currently in good standing without refunds."}
              </p>
            </div>
          ) : (
            <table className="admin-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th>Order Reference</th>
                  <th>Customer Information</th>
                  <th>Refund Type</th>
                  <th className="text-end">Disbursed Amount</th>
                  <th>Processed Date</th>
                  <th>Origin / Actor</th>
                  <th className="text-end" style={{ width: "120px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((item, index) => {
                  const isFull = isItemFullRefund(item);
                  const customerName = `${item.firstname || ""} ${item.lastname || ""}`.trim() || "Guest Customer";
                  const initial = customerName.charAt(0).toUpperCase();
                  const refundedAmt = parseFloat(item.total_refunded || 0);
                  const orderTotalAmt = parseFloat(item.total_amount || 0);

                  return (
                    <tr key={index}>
                      <td className="text-muted fw-semibold">{index + 1}</td>
                      <td>
                        <div className="fw-bold text-dark font-monospace">
                          {item.tracking_no || `ORD-#${item.order_id}`}
                        </div>
                        <span className="text-muted small">Order ID: #{item.order_id}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center fw-bold"
                            style={{ width: "32px", height: "32px", fontSize: "13px" }}
                          >
                            {initial}
                          </div>
                          <div>
                            <div className="fw-semibold text-dark">{customerName}</div>
                            <div className="text-muted small d-flex align-items-center gap-2">
                              {item.email && <span>{item.email}</span>}
                              {item.phone && <span>• {item.phone}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {isFull ? (
                          <span className="admin-badge admin-badge-danger">
                            <FaUndoAlt size={10} /> FULL REFUND
                          </span>
                        ) : (
                          <span className="admin-badge admin-badge-warning">
                            <FaBoxOpen size={10} /> PARTIAL REFUND
                          </span>
                        )}
                      </td>
                      <td className="text-end font-monospace">
                        <div className="fw-bold text-danger">
                          Rs. {refundedAmt.toLocaleString()}
                        </div>
                        {orderTotalAmt > 0 && orderTotalAmt !== refundedAmt && (
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            of Rs. {orderTotalAmt.toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-medium text-dark">
                          {moment(item.created_at).isValid() ? moment(item.created_at).format("DD MMM YYYY") : "—"}
                        </div>
                        <div className="text-muted small">
                          {moment(item.created_at).isValid() ? moment(item.created_at).format("hh:mm A") : "—"}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border px-2 py-1 fw-normal">
                          {item.created_by || "System Admin"}
                        </span>
                      </td>
                      <td className="text-end">
                        <Link
                          to={`/admin/refund-order-details/${item.order_id}`}
                          className="btn btn-sm btn-outline-primary fw-semibold d-inline-flex align-items-center gap-1 rounded-3 px-2.5 py-1"
                        >
                          Details <FaArrowRight size={11} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
